import { useState, useEffect, useRef } from 'react';
import { 
  Headphones, 
  Play, 
  Pause, 
  Volume2, 
  Clock,
  Calendar,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { useSeo } from '../components/custom/Seo';
import axios from 'axios';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Podcast = () => {
  useSeo({
    title: 'DC Sports Report Podcast | Washington DC Sports Talk',
    description:
      'Listen to the DC Sports Report Podcast — in-depth analysis and conversation on the Commanders, Nationals, Capitals, Wizards and all things Washington, D.C. sports.',
    path: '/podcast',
  });
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState([75]);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchEpisodes();
  }, []);

  const fetchEpisodes = async () => {
    try {
      const response = await axios.get(`${API}/podcast/episodes`);
      setEpisodes(response.data);
    } catch (error) {
      logError('Error fetching episodes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = (episode) => {
    if (currentEpisode?.title === episode.title) {
      if (isPlaying) {
        audioRef.current?.pause();
      } else {
        audioRef.current?.play();
      }
      setIsPlaying(!isPlaying);
    } else {
      setCurrentEpisode(episode);
      setIsPlaying(true);
      setTimeout(() => {
        audioRef.current?.play();
      }, 100);
    }
  };

  const handleVolumeChange = (value) => {
    setVolume(value);
    if (audioRef.current) {
      audioRef.current.volume = value[0] / 100;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div data-testid="podcast-page" className="min-h-screen bg-[#002868]">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1660832458429-526d0601054f?w=1200&h=600&fit=crop)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#002868] via-[#002868]/90 to-[#002868]" />
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            {/* Podcast Art */}
            <div className="relative w-48 h-48 sm:w-64 sm:h-64 flex-shrink-0">
              <div className="absolute inset-0 bg-[#FFB612] border-2 border-white shadow-brutal-lg flex items-center justify-center">
                <Headphones className="w-24 h-24 text-[#002868]" />
              </div>
              <div className="absolute -bottom-2 -right-2 w-full h-full bg-[#BF0A30] border-2 border-white -z-10" />
            </div>

            {/* Info */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-[#BF0A30] text-white px-4 py-1 text-xs font-bold uppercase tracking-widest mb-4 border-2 border-white shadow-brutal-sm">
                <Play className="w-4 h-4" fill="currentColor" />
                Podcast
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-white uppercase tracking-tight leading-none mb-4">
                DC Sports<br />
                <span className="text-[#FFB612]">Report Podcast</span>
              </h1>
              
              <p className="text-slate-400 text-lg max-w-xl">
                Your weekly deep dive into DC sports. Covering the Commanders, Capitals, 
                Nationals, and Wizards with analysis, interviews, and hot takes.
              </p>

              <div className="flex flex-wrap gap-4 mt-6 justify-center md:justify-start">
                <a 
                  href="https://api.riverside.fm/hosting/Z6N0nxGa.rss"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="rss-feed-link"
                >
                  <Button
                    className="h-12 px-6 bg-[#FFB612] hover:bg-[#e5a410] text-[#002868] font-bold uppercase tracking-wide border-2 border-white shadow-brutal hover:shadow-brutal-lg hover:-translate-y-1 transition-all"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    RSS Feed
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Accent */}
        <div className="h-2 bg-gradient-to-r from-[#FFB612] via-white to-[#FFB612]" />
      </section>

      {/* Episodes Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <h2 className="font-heading text-3xl text-white uppercase tracking-tight mb-8">
          Latest Episodes
        </h2>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#FFB612] animate-spin" />
          </div>
        ) : episodes.length === 0 ? (
          <Card className="border-2 border-[#5A1414] bg-[#002868]">
            <CardContent className="p-8 text-center">
              <Headphones className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-300">No episodes available yet. Check back soon!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {episodes.map((episode, index) => (
              <Card 
                key={episode.audio_url || episode.title}
                data-testid={`episode-card-${index}`}
                className={`border-2 transition-all ${
                  currentEpisode?.title === episode.title 
                    ? 'border-[#FFB612] bg-[#001a42]' 
                    : 'border-[#5A1414] bg-[#001a42]/50 hover:border-[#BF0A30]'
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Play Button */}
                    <button
                      onClick={() => handlePlayPause(episode)}
                      data-testid={`play-button-${index}`}
                      disabled={!episode.audio_url}
                      className={`flex-shrink-0 w-14 h-14 flex items-center justify-center border-2 border-white transition-all ${
                        episode.audio_url 
                          ? 'bg-[#FFB612] hover:bg-[#e5a410] shadow-brutal hover:shadow-brutal-sm' 
                          : 'bg-slate-600 cursor-not-allowed'
                      }`}
                    >
                      {currentEpisode?.title === episode.title && isPlaying ? (
                        <Pause className="w-6 h-6 text-[#002868]" fill="currentColor" />
                      ) : (
                        <Play className="w-6 h-6 text-[#002868] ml-1" fill="currentColor" />
                      )}
                    </button>

                    {/* Episode Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white text-lg mb-1 truncate">
                        {episode.title}
                      </h3>
                      <p className="text-slate-400 text-sm line-clamp-2 mb-3">
                        {episode.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        {episode.pub_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(episode.pub_date)}
                          </span>
                        )}
                        {episode.duration && episode.duration !== 'Unknown' && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {episode.duration}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Audio Player (Fixed Bottom) */}
      {currentEpisode && currentEpisode.audio_url && (
        <div 
          data-testid="audio-player"
          className="fixed bottom-0 left-0 right-0 bg-[#001a42] border-t-4 border-[#FFB612] p-4 z-50"
        >
          <div className="max-w-7xl mx-auto flex items-center gap-4">
            {/* Play/Pause */}
            <button
              onClick={() => handlePlayPause(currentEpisode)}
              className="flex-shrink-0 w-12 h-12 bg-[#FFB612] hover:bg-[#e5a410] border-2 border-white flex items-center justify-center shadow-brutal-sm transition-all"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 text-[#002868]" fill="currentColor" />
              ) : (
                <Play className="w-5 h-5 text-[#002868] ml-0.5" fill="currentColor" />
              )}
            </button>

            {/* Title */}
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold truncate">{currentEpisode.title}</p>
              <p className="text-slate-400 text-sm">DC Sports Report Podcast</p>
            </div>

            {/* Volume */}
            <div className="hidden sm:flex items-center gap-2 w-32">
              <Volume2 className="w-5 h-5 text-slate-400" />
              <Slider
                value={volume}
                onValueChange={handleVolumeChange}
                max={100}
                step={1}
                className="w-full"
              />
            </div>
          </div>

          {/* Hidden Audio Element */}
          <audio
            ref={audioRef}
            src={currentEpisode.audio_url}
            onEnded={() => setIsPlaying(false)}
            onError={() => setIsPlaying(false)}
          />
        </div>
      )}

      {/* Spacer for fixed player */}
      {currentEpisode && currentEpisode.audio_url && (
        <div className="h-24" />
      )}
    </div>
  );
};

export default Podcast;
